import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import ms from 'ms'
import { cognition, filters } from '@adc/blueprint'
import {
  Modal,
  Icon,
  Button,
  Text,
  SelectField,
  utils,
  Loader,
  useApplicationNotifications,
  UnsavedChangesModal,
  coreConstants,
  coreHooks,
  CopyButton,
  NOTIFICATION_ROLES,
} from '@adc/ui-morpheus'
// @ts-ignore no types
import type {
  CognitionEvaluationGroup,
  CognitionEvaluationRubric,
  CognitionEvaluationMethod,
  User,
  CognitionEvaluationScore,
  Party,
  CognitionEvaluationJudge,
  Actor,
} from '@adc/sdk/types'
import classnames from 'classnames/bind'
import StatusPill from '../statusPill'
import UserValue from '../userValue'
import Trace from '../trace/'
import CurrentUserRatingSection from '../currentUserRatingSection'
import KeyboardShortcutsGuide from '../keyboardShortcutsGuide'
import OthersEvaluationsSection from '../othersEvaluationsSection'
import ExternalUserFeedbackSection from '../externalUserFeedbackSection'
import ListingLinks from '../../../components/listingLinks'
import styles from './styles.scss'

const { ColorNames } = coreConstants // eslint-disable-line

const SCROLL_AMOUNT_PER_KEY_PRESS = 200 // Amount to scroll on each key press
const POST_LOAD_WAIT_MS = ms('100ms') // Wait time for modal focus after content loads

const cx = classnames.bind(styles)

// Internal domains for user classification
const INTERNAL_DOMAINS = ['auction.com', 'redcmail.com']

// Helper function to check if email is from external domain
const isExternalUser = (email: string | undefined): boolean => {
  if (!email) return false
  const domain = email.split('@')[1]?.toLowerCase()
  return domain ? !INTERNAL_DOMAINS.includes(domain) : false
}

// Type guard for CognitionEvaluationJudge
const isJudgeEvaluator = (
  evaluator: Actor | null | undefined
): evaluator is CognitionEvaluationJudge => {
  return evaluator?.__typename === 'CognitionEvaluationJudge'
}

// Type guard for Party
const isPartyEvaluator = (
  evaluator: Actor | null | undefined
): evaluator is Party => {
  return evaluator?.__typename === 'Party'
}

// Safely extract URN from any Actor (SalesforceUser doesn't have urn)
const getEvaluatorUrn = (evaluator: Actor | null | undefined): string => {
  if (!evaluator) return ''
  return 'urn' in evaluator ? String(evaluator.urn) : ''
}

// Get display name for any Actor
const getEvaluatorDisplayName = (rubric: CognitionEvaluationRubric): string => {
  const { evaluator } = rubric

  if (isJudgeEvaluator(evaluator)) {
    return evaluator.description
  }

  if (isPartyEvaluator(evaluator)) {
    return evaluator.user?.email || 'Unknown Evaluator'
  }

  return 'Unknown Evaluator'
}

// Get secondary display text
const getEvaluatorSecondaryText = (
  rubric: CognitionEvaluationRubric
): string => {
  const { evaluator } = rubric

  if (isJudgeEvaluator(evaluator)) {
    return 'Automated Judge'
  }

  if (isPartyEvaluator(evaluator)) {
    return evaluator.user?.display_name || 'Unspecified'
  }

  return 'Unspecified'
}

type Rating = 'thumbsUp' | 'thumbsDown' | null

interface EvaluationDetailModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  evaluationData: CognitionEvaluationGroup | null
  currentIndex: number
  totalCount: number
  onPrevious: () => void
  onNext: () => void
  hasPrevious: boolean
  hasNext: boolean
  isLoading?: boolean
  user: User
}

const EvaluationDetailModal: React.FC<EvaluationDetailModalProps> = ({
  isOpen,
  onClose,
  onSave,
  evaluationData,
  currentIndex,
  totalCount,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  isLoading,
  user,
}): React.ReactElement => {
  const notifications = useApplicationNotifications() // eslint-disable-line
  const breakpoint = coreHooks.useBreakpoints() // eslint-disable-line

  /**
   * STATE
   */
  const [selectedMethod, setSelectedMethod] =
    useState<CognitionEvaluationMethod | null>(null)
  const [selectedEvaluatorUrn, setSelectedEvaluatorUrn] = useState<string>('')
  const [prevSelectedMethod, setPrevSelectedMethod] =
    useState<CognitionEvaluationMethod | null>(null)
  const [wasWinnerSelected, setWasWinnerSelected] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false)
  const [isShortcutsGuideVisible, setIsShortcutsGuideVisible] =
    useState<boolean>(false)
  // Track user scores across methods locally
  const [localUserScores, setLocalUserScores] = useState<
    Record<string, { rating: Rating; comment: string }>
  >({})

  /**
   * REFS
   */
  // Track comment value without causing re-renders
  const commentValueRef = React.useRef<string>('')
  // Track current rating value without causing re-renders
  const currentRatingRef = React.useRef<Rating>(null)
  // Track if comment had focus before opening confirm modal
  const wasCommentFocusedRef = React.useRef<boolean>(false)
  const modalRef = React.useRef<HTMLDivElement>(null)
  const traceContainerRef = React.useRef<HTMLDivElement>(null)
  const commentTextAreaRef = React.useRef<HTMLTextAreaElement>(null)
  // Track initial values from server for dirty checking
  const initialScoresRef = React.useRef<
    Record<string, { rating: Rating; comment: string }>
  >({})
  const winningMethod = evaluationData?.evaluations?.find(
    evaluation => evaluation.is_winner
  )?.method

  // Derive current rating and comment from localUserScores
  const currentScore = selectedMethod ? localUserScores[selectedMethod] : null
  const userRating = currentScore?.rating || null
  const userComment = currentScore?.comment || ''

  /**
   * Helper to get current values and dirty state
   * This is intentionally not directly dependent on react-state to ensure most
   * up-to-date values are used (from refs)
   */
  const getCurrentValues = (
    selectedMethod: CognitionEvaluationMethod | null
  ) => {
    const currentComment = commentValueRef.current
    const currentRating = currentRatingRef.current
    const initialRating = selectedMethod
      ? initialScoresRef.current[selectedMethod]?.rating || null
      : null
    const initialComment = selectedMethod
      ? initialScoresRef.current[selectedMethod]?.comment || ''
      : ''

    const hasRatingChanged = currentRating !== initialRating
    const hasCommentChanged = currentComment !== initialComment
    const isActuallyDirty = hasRatingChanged || hasCommentChanged
    return {
      currentComment,
      currentRating,
      initialRating,
      initialComment,
      hasRatingChanged,
      hasCommentChanged,
      isActuallyDirty,
    }
  }

  // Initialize selectedMethod when evaluationData changes
  useEffect(() => {
    if (!evaluationData?.evaluations?.length) return

    let methodToSelect = null

    // Navigation logic
    if (wasWinnerSelected && winningMethod) {
      // If winner was selected previously, try to select winner again
      methodToSelect = winningMethod
    } else if (prevSelectedMethod) {
      // Try to select the same method as before
      const methodExists = evaluationData.evaluations.some(
        e => e.method === prevSelectedMethod
      )
      if (methodExists) {
        methodToSelect = prevSelectedMethod
      } else if (winningMethod) {
        // Fallback to winner
        methodToSelect = winningMethod
      } else {
        // Last resort: first method
        methodToSelect = evaluationData.evaluations[0].method
      }
    } else {
      // Initial load: select winner or first
      methodToSelect = winningMethod || evaluationData.evaluations[0].method
    }

    setSelectedMethod(methodToSelect)
  }, [evaluationData, prevSelectedMethod, wasWinnerSelected, winningMethod])

  // Update tracking states when selectedMethod changes
  useEffect(() => {
    if (selectedMethod) {
      setPrevSelectedMethod(selectedMethod)
      setWasWinnerSelected(selectedMethod === winningMethod)
    }
  }, [selectedMethod, winningMethod])

  // Track the current evaluation URN to know when to reset
  const currentEvaluationUrnRef = React.useRef<string | null>(null)

  // Initialize localUserScores only when evaluationData changes to a different evaluation
  useEffect(() => {
    if (evaluationData && user?.party_id) {
      // Check if this is a different evaluation
      const isDifferentEvaluation =
        currentEvaluationUrnRef.current !== evaluationData.urn

      if (isDifferentEvaluation) {
        currentEvaluationUrnRef.current = evaluationData.urn

        // Initialize local scores with server data
        const initialScores: Record<
          string,
          { rating: Rating; comment: string }
        > = {}

        evaluationData.evaluations?.forEach(evaluation => {
          if (evaluation.method) {
            // Check if user has a rubric for this method (user is always Party)
            const userRubric = evaluation.rubrics?.find(
              rubric =>
                isPartyEvaluator(rubric.evaluator) &&
                rubric.evaluator.party_id === user.party_id
            )

            if (userRubric) {
              // eslint-disable-next-line no-nested-ternary
              const rating = userRubric.score
                ? 'thumbsUp'
                : userRubric.score === false
                ? 'thumbsDown'
                : null
              initialScores[evaluation.method] = {
                rating,
                comment: userRubric.notes || '',
              }
            } else {
              // No existing rating for this method
              initialScores[evaluation.method] = {
                rating: null,
                comment: '',
              }
            }
          }
        })

        setLocalUserScores(initialScores)
        initialScoresRef.current = { ...initialScores } // Store copy for dirty checking
        // Sync refs with the current method's data
        if (selectedMethod && initialScores[selectedMethod]) {
          commentValueRef.current = initialScores[selectedMethod].comment
          currentRatingRef.current = initialScores[selectedMethod].rating
        } else {
          commentValueRef.current = ''
          currentRatingRef.current = null
        }
        setShouldFocusComment(false)
      }
    }
  }, [evaluationData?.urn, user?.party_id, selectedMethod]) // eslint-disable-line

  // Create method options for dropdown
  const methodOptions = useMemo<
    Array<{ text: React.ReactNode; value: CognitionEvaluationMethod }>
  >(() => {
    if (!evaluationData?.evaluations) return []

    return evaluationData.evaluations.map(evaluation => ({
      text: (
        <div style={{ width: '100%' }}>
          {evaluation.method_version}
          {evaluation.is_winner && (
            <>
              {' '}
              <Icon
                name='trophy'
                iconStyle='solid'
                color={ColorNames.SunYellow} // eslint-disable-line
                size='medium'
                style={{ fontSize: '14px' }}
              />
            </>
          )}
        </div>
      ),
      value: evaluation.method,
    }))
  }, [evaluationData])

  // Compute the detail URL for sharing
  const detailURL = useMemo(() => {
    if (!evaluationData?.urn) return ''

    // set to search on URN only
    const serializedViewState = filters.utils.serializePageParams({
      searchText: evaluationData.urn,
    })

    const url = new URL(window.location.href)
    url.searchParams.set('view_state', serializedViewState)
    url.searchParams.set('auto_open', '0')
    return url.toString()
  }, [evaluationData?.urn])

  // Find external user's rubric and get all rubrics
  const { externalUserRubric } = useMemo(() => {
    if (!evaluationData?.evaluations) {
      return { externalUserRubric: null, allRubrics: [] }
    }

    // Collect all rubrics from all evaluations
    const allRubricsArray = evaluationData.evaluations.flatMap(
      evaluation => evaluation.rubrics || []
    )

    // Check if created_by user is external
    const createdByEmail = evaluationData.created_by_user?.email
    const isExternal = isExternalUser(createdByEmail)

    let externalRubric: CognitionEvaluationRubric | null = null

    if (isExternal && createdByEmail) {
      // Find rubric where evaluator matches the external created_by user (must be Party)
      externalRubric =
        allRubricsArray.find(
          rubric =>
            isPartyEvaluator(rubric.evaluator) &&
            rubric.evaluator.user?.email === createdByEmail
        ) || null
    }

    return {
      externalUserRubric: externalRubric,
      allRubrics: allRubricsArray,
    }
  }, [evaluationData])

  // Get rubrics for the selected method only
  const methodRubrics = useMemo(() => {
    if (!evaluationData?.evaluations || !selectedMethod) {
      return []
    }

    const methodEvaluation = evaluationData.evaluations.find(
      evaluation => evaluation.method === selectedMethod
    )

    return methodEvaluation?.rubrics || []
  }, [evaluationData, selectedMethod])

  // Filter rubrics for the selected method (exclude external user and current user)
  const filteredRubrics = useMemo(() => {
    let filtered = methodRubrics

    // Filter out the external user's rubric if they exist
    if (externalUserRubric) {
      filtered = filtered.filter(
        rubric =>
          getEvaluatorUrn(rubric.evaluator) !==
          getEvaluatorUrn(externalUserRubric.evaluator)
      )
    }

    // Filter out the current user's rubric (user is always Party)
    if (user?.party_id) {
      filtered = filtered.filter(
        rubric =>
          !(
            isPartyEvaluator(rubric.evaluator) &&
            rubric.evaluator.party_id === user.party_id
          )
      )
    }

    return filtered
  }, [methodRubrics, externalUserRubric, user?.party_id])

  // Create evaluator dropdown options
  const evaluatorOptions = useMemo<
    Array<{ text: React.ReactNode; value: string }>
  >(
    () =>
      filteredRubrics.map(rubric => {
        const displayName = getEvaluatorDisplayName(rubric)
        const secondaryText = getEvaluatorSecondaryText(rubric)
        const icon = (
          <Icon
            iconStyle='solid'
            name={rubric.score ? 'thumbs-up' : 'thumbs-down'}
            color={rubric.score ? 'success' : 'error'}
          />
        )

        return {
          text: displayName,
          value: getEvaluatorUrn(rubric.evaluator),
          content: (
            <div className={cx('rich-item')}>
              <div className={cx('rich-item-header')}>
                <Text
                  size='small'
                  weight='heavy'
                  className={cx('rich-item-title')}
                >
                  {displayName}
                </Text>
                {icon}
              </div>
              <Text size='small' color='gray' style={{ width: '100%' }}>
                {secondaryText}
              </Text>
            </div>
          ),
        }
      }),
    [filteredRubrics]
  )

  // Initialize selected evaluator when data or method changes
  useEffect(() => {
    if (!filteredRubrics.length) {
      setSelectedEvaluatorUrn('')
      return
    }

    // Try to select the current user's rubric (if they're a Party evaluator)
    const currentUserRubric = filteredRubrics.find(
      rubric =>
        isPartyEvaluator(rubric.evaluator) &&
        rubric.evaluator.party_id === user?.party_id
    )

    if (currentUserRubric) {
      setSelectedEvaluatorUrn(getEvaluatorUrn(currentUserRubric.evaluator))
    } else {
      setSelectedEvaluatorUrn(getEvaluatorUrn(filteredRubrics[0].evaluator))
    }
  }, [filteredRubrics, user?.party_id, selectedMethod])

  // Get the selected rubric based on selectedEvaluatorUrn and selected method
  const selectedRubric = useMemo(
    () =>
      methodRubrics.find(
        rubric => getEvaluatorUrn(rubric.evaluator) === selectedEvaluatorUrn
      ),
    [methodRubrics, selectedEvaluatorUrn]
  )

  // Sync refs when method changes
  useEffect(() => {
    if (selectedMethod && localUserScores[selectedMethod]) {
      commentValueRef.current = localUserScores[selectedMethod].comment
      currentRatingRef.current = localUserScores[selectedMethod].rating
    } else {
      commentValueRef.current = ''
      currentRatingRef.current = null
    }
  }, [selectedMethod, localUserScores])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset UI state when modal closes, but preserve scores
      commentValueRef.current = ''
      currentRatingRef.current = null
      setShouldFocusComment(false) // Reset focus flag
      setIsConfirmModalOpen(false) // Reset confirm modal
      // Don't reset localUserScores here - preserve them across close/open
    } else {
      // When modal opens, sync refs with current method's data
      if (selectedMethod && localUserScores[selectedMethod]) {
        commentValueRef.current = localUserScores[selectedMethod].comment
        currentRatingRef.current = localUserScores[selectedMethod].rating
      }
    }
  }, [isOpen, selectedMethod, localUserScores])

  // Track if rating was just clicked vs loaded
  const [shouldFocusComment, setShouldFocusComment] = useState(false)

  // Auto-focus comment textarea only when rating is actively selected by user
  useEffect(() => {
    if (shouldFocusComment && userRating && commentTextAreaRef.current) {
      // Small delay to ensure the textarea is enabled first
      setTimeout(() => {
        commentTextAreaRef.current?.focus()
        setShouldFocusComment(false) // Reset flag
      }, 200)
    }
  }, [shouldFocusComment, userRating])

  // Memoized comment change handler to prevent focus loss
  const handleCommentChange = useCallback((value: string) => {
    commentValueRef.current = value
    // Don't update state while typing to prevent re-renders and focus loss
  }, [])

  // Memoized rating handlers
  const handleThumbsUp = useCallback(() => {
    if (selectedMethod) {
      currentRatingRef.current = 'thumbsUp' // Update ref immediately
      setLocalUserScores(prev => ({
        ...prev,
        [selectedMethod]: {
          ...prev[selectedMethod],
          rating: 'thumbsUp',
          comment: commentValueRef.current,
        },
      }))
    }
  }, [selectedMethod])

  const handleThumbsDown = useCallback(() => {
    if (selectedMethod) {
      currentRatingRef.current = 'thumbsDown' // Update ref immediately
      setLocalUserScores(prev => ({
        ...prev,
        [selectedMethod]: {
          ...prev[selectedMethod],
          rating: 'thumbsDown',
          comment: commentValueRef.current,
        },
      }))
      setShouldFocusComment(true) // Set flag to auto-focus comment after user clicks
    }
  }, [selectedMethod])

  // Save rating function
  const saveRating = useCallback(async (): Promise<boolean> => {
    if (!selectedMethod || !evaluationData?.urn) return true

    const { currentComment, currentRating, isActuallyDirty } =
      getCurrentValues(selectedMethod)

    // Skip save if nothing changed or no rating selected
    if (!isActuallyDirty || currentRating === null) {
      // Sync comment to state even if not saving
      if (selectedMethod) {
        setLocalUserScores(prev => ({
          ...prev,
          [selectedMethod]: {
            ...prev[selectedMethod],
            comment: currentComment,
          },
        }))
      }
      return true
    }

    setIsSaving(true)
    onSave()
    try {
      const score: CognitionEvaluationScore = {
        score: currentRating === 'thumbsUp',
        notes: currentComment || undefined,
      }

      await cognition.utils.scoreEvaluation({
        urn: evaluationData.urn,
        method: selectedMethod,
        score,
      })

      // Update initial values after successful save
      initialScoresRef.current[selectedMethod] = {
        rating: currentRating,
        comment: currentComment,
      }

      // Update local scores - ensure we preserve the structure
      setLocalUserScores(prev => {
        const updated = {
          ...prev,
          [selectedMethod]: {
            rating: currentRating,
            comment: currentComment,
          },
        }
        return updated
      })

      return true
    } catch (error) {
      // eslint-disable-next-line
      notifications.toast.open({
        role: NOTIFICATION_ROLES.ERROR,
        title: 'Error saving scoring information',
      })
      return false
    } finally {
      setIsSaving(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    evaluationData?.urn,
    selectedMethod,
    localUserScores,
    notifications,
    onSave,
  ])

  const handleOnCloseAndReset = useCallback(() => {
    setSelectedMethod(null)
    onClose()
  }, [onClose])

  // Handle modal close with unsaved changes prompt
  const handleClose = useCallback(() => {
    if (!selectedMethod) {
      handleOnCloseAndReset()
      return
    }

    const { isActuallyDirty } = getCurrentValues(selectedMethod)

    if (isActuallyDirty) {
      // Track if comment was focused and blur it before opening the modal
      wasCommentFocusedRef.current =
        document.activeElement === commentTextAreaRef.current
      if (commentTextAreaRef.current) {
        commentTextAreaRef.current.blur()
      }
      setIsConfirmModalOpen(true)
    } else {
      handleOnCloseAndReset()
    }
  }, [selectedMethod, localUserScores, handleOnCloseAndReset]) // eslint-disable-line

  // Handlers for UnsavedChangesModal
  const onContinueWithoutSaving = useCallback(() => {
    setIsConfirmModalOpen(false)
    setSelectedMethod(null)
    handleOnCloseAndReset()
  }, [handleOnCloseAndReset])

  const onKeepEditing = useCallback(() => {
    setIsConfirmModalOpen(false)
    // Restore focus to comment textarea if it was previously focused
    // Use a small delay to ensure the modal is closed first
    if (wasCommentFocusedRef.current) {
      setTimeout(() => {
        if (commentTextAreaRef.current && userRating) {
          commentTextAreaRef.current.focus()
        }
      }, 100)
    }
  }, [userRating])

  const onSaveAndContinue = useCallback(async () => {
    setIsConfirmModalOpen(false)
    const saved = await saveRating()
    if (saved) {
      handleOnCloseAndReset()
    }
  }, [saveRating, handleOnCloseAndReset])

  // Focus modal after loading completes
  useEffect(() => {
    if (!isLoading && isOpen && modalRef.current) {
      // Focus after a short delay to ensure content is rendered
      const focusTimer = setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus()
        }
      }, POST_LOAD_WAIT_MS)

      return () => clearTimeout(focusTimer)
    }
    return undefined
  }, [isLoading, isOpen])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !modalRef.current) return

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if modal or its children have focus
      if (
        !modalRef.current ||
        !modalRef.current.contains(document.activeElement)
      ) {
        return
      }

      // Handle SHIFT+ENTER for next navigation (works even in textarea)
      if (event.shiftKey && event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        if (hasNext) {
          saveRating()
            .then(saved => {
              if (saved) {
                onNext()
              }
              return null
            })
            .catch(error => {
              console.error('Failed to save rating before navigation:', error)
            })
        }
        return
      }

      // Handle SHIFT + arrow keys for smooth scrolling (works even in textarea)
      if (
        event.shiftKey &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown')
      ) {
        event.preventDefault()
        event.stopPropagation()

        if (traceContainerRef.current) {
          const scrollAmount = SCROLL_AMOUNT_PER_KEY_PRESS
          const currentScroll = traceContainerRef.current.scrollTop
          const newScroll =
            event.key === 'ArrowUp'
              ? currentScroll - scrollAmount
              : currentScroll + scrollAmount

          traceContainerRef.current.scrollTo({
            top: newScroll,
            behavior: 'smooth',
          })
        }
        return
      }

      // Don't handle other keyboard shortcuts if typing in textarea
      if (document.activeElement?.id === 'user-rating-comment') {
        return
      }

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault()
          event.stopPropagation()
          if (hasPrevious) {
            saveRating()
              .then(saved => {
                if (saved) {
                  onPrevious()
                }
                return null
              })
              .catch(error => {
                console.error('Failed to save rating before navigation:', error)
              })
          }
          break
        case 'ArrowDown':
          event.preventDefault()
          event.stopPropagation()
          if (hasNext) {
            saveRating()
              .then(saved => {
                if (saved) {
                  onNext()
                }
                return null
              })
              .catch(error => {
                console.error('Failed to save rating before navigation:', error)
              })
          }
          break
        case '=':
        case '+':
          event.preventDefault()
          event.stopPropagation()
          handleThumbsUp()
          break
        case '-':
        case '_':
          event.preventDefault()
          event.stopPropagation()
          handleThumbsDown()
          break
        case 'c':
        case 'C':
          event.preventDefault()
          event.stopPropagation()
          if (commentTextAreaRef.current) {
            commentTextAreaRef.current.focus()
            commentTextAreaRef.current.select()
          }
          break
        case '/':
          event.preventDefault()
          event.stopPropagation()
          setIsShortcutsGuideVisible(prev => !prev)
          break
        default:
          break
      }
    }

    // Focus the modal when it opens
    const focusTimer = setTimeout(() => {
      if (modalRef.current) {
        modalRef.current.focus()
      }
    }, 100)

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      clearTimeout(focusTimer)
    }
  }, [
    isOpen,
    hasPrevious,
    hasNext,
    onPrevious,
    onNext,
    traceContainerRef,
    saveRating,
    handleThumbsUp,
    handleThumbsDown,
    setIsShortcutsGuideVisible,
  ])
  // @ts-ignore CognitionEvaluationGroup doesn't have assignee yet
  const assignee = evaluationData?.assignee as Party | undefined // eslint-disable-line
  const listingContext = evaluationData?.context?.sources?.find(source =>
    source.startsWith('adc:listing')
  )
  const listingId = listingContext && listingContext.replace('adc:listing:', '')
  // Offer URN in the eval context (e.g. adc:reo_offer:{offerId}). When present
  // alongside a listing, surface a link to the Morpheus closing view.
  const hasOfferContext = Boolean(
    evaluationData?.context?.sources?.some(source =>
      source.startsWith('adc:reo_offer')
    )
  )

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        size='fullscreen'
        elmId='evaluation_detail_modal'
        closeIcon={
          <Icon
            className='close'
            data-elm-id='evaluation_detail_modal_close_button'
            name='times'
            iconStyle='regular'
            fitted
            onClick={handleClose}
          />
        }
        className={cx('modal')}
      >
        <Modal.Content>
          <div ref={modalRef} tabIndex={-1} className={cx('content-wrapper')}>
            <Loader
              active={isLoading}
              elmId='loading_spinner'
              dimmer={false}
              inverted
              className={cx('loader')}
            />

            {!evaluationData ? (
              <div className={cx('modal-content')}>
                <div
                  className={cx('no-data-message')}
                  data-elm-id='no_data_message'
                >
                  <Text size='large' color='gray'>
                    No data available for this row.
                  </Text>
                </div>
              </div>
            ) : (
              <div className={cx('modal-content')}>
                <div className={cx('column-wrapper')}>
                  <div className={cx('left-column')}>
                    <div className={cx('header-row')}>
                      <Text
                        size='huge'
                        weight='heavy'
                        className={cx('section-title')}
                      >
                        Run Details
                      </Text>
                      <CopyButton
                        text={detailURL}
                        size='small'
                        elmId='copy-detail-url'
                        showSuccessTextInButton
                        defaultIconName='link'
                      />
                      {evaluationData.status && (
                        <StatusPill
                          status={evaluationData.status}
                          style={{ marginLeft: 'auto' }}
                        />
                      )}
                    </div>

                    <div className={cx('content')}>
                      <div>
                        <div className={cx('detail-row')}>
                          <Text
                            size='small'
                            color='gray'
                            style={{ marginRight: '12px' }}
                          >
                            Method:
                          </Text>
                          <div style={{ flex: 1 }}>
                            <SelectField
                              className={cx('select-field')}
                              hideWrapper
                              value={selectedMethod}
                              onChange={async (
                                _e: unknown,
                                data: { value: string }
                              ) => {
                                // Save current rating before switching methods
                                const saved = await saveRating()
                                if (saved) {
                                  setSelectedMethod(
                                    data?.value as CognitionEvaluationMethod
                                  )
                                }
                              }}
                              options={methodOptions}
                              compact
                              elmId='method'
                            />
                          </div>
                        </div>

                        <div className={cx('detail-row')}>
                          <Text size='small' color='gray'>
                            Owner:
                          </Text>
                          <Text size='small' className={cx('value-text')}>
                            <UserValue
                              email={
                                evaluationData.owner?.__typename === 'Party'
                                  ? evaluationData.owner?.user?.email || ''
                                  : ''
                              }
                              display_name={
                                evaluationData.owner?.__typename === 'Party'
                                  ? evaluationData.owner?.user?.display_name ||
                                    ''
                                  : ''
                              }
                              nullDisplay='Unspecified'
                            />
                          </Text>
                        </div>

                        <div className={cx('detail-row')}>
                          <Text size='small' color='gray'>
                            Created Date:
                          </Text>
                          <Text size='small'>
                            {/* eslint-disable-next-line */}
                            {utils.date.formatDateApiToUi(
                              evaluationData.created_date || '',
                              'MMM DD, YYYY hh:mm A'
                            )}
                          </Text>
                        </div>
                        {evaluationData.data_set?.description && (
                          <div className={cx('detail-row')}>
                            <Text size='small' color='gray'>
                              Data Set:
                            </Text>
                            <Text size='small' className={cx('value-text')}>
                              {evaluationData.data_set.description}
                            </Text>
                          </div>
                        )}
                        {listingId && (
                          <div className={cx('detail-row')}>
                            <Text size='small' color='gray'>
                              Listing Context:
                            </Text>
                            <div className={cx('listing-context')}>
                              <Text size='small' color='gray'>
                                {listingId}
                              </Text>
                              <ListingLinks
                                listing={{
                                  listing_id: listingId,
                                }}
                                label=''
                                showAdcLinkAlways
                                showClosingLink={hasOfferContext}
                                elmIdPrefix='listing_context'
                              />
                            </div>
                          </div>
                        )}
                        {assignee && (
                          <div className={cx('detail-row')}>
                            <Text size='small' color='gray'>
                              Assigned To:
                            </Text>
                            <Text size='small' className={cx('value-text')}>
                              <UserValue
                                email={
                                  assignee?.web_communications?.[0]
                                    ?.identifier || ''
                                }
                                display_name={
                                  `${assignee?.person?.first_name} ${assignee?.person?.last_name}` ||
                                  ''
                                }
                                nullDisplay='Unspecified'
                              />
                            </Text>
                          </div>
                        )}
                      </div>

                      {externalUserRubric && (
                        <ExternalUserFeedbackSection
                          rubric={externalUserRubric}
                        />
                      )}

                      <CurrentUserRatingSection
                        userRating={userRating}
                        userComment={userComment}
                        isSaving={isSaving}
                        onThumbsUp={handleThumbsUp}
                        onThumbsDown={handleThumbsDown}
                        onCommentChange={handleCommentChange}
                        commentTextAreaRef={commentTextAreaRef}
                      />

                      <OthersEvaluationsSection
                        filteredRubrics={filteredRubrics}
                        selectedEvaluatorId={selectedEvaluatorUrn}
                        onEvaluatorChange={setSelectedEvaluatorUrn}
                        evaluatorOptions={evaluatorOptions}
                        selectedRubric={selectedRubric}
                      />
                    </div>

                    <div className={cx('left-footer')}>
                      <div className={cx('navigation-section')}>
                        <Button
                          onClick={async () => {
                            const saved = await saveRating()
                            if (saved) {
                              onPrevious()
                            }
                          }}
                          disabled={!hasPrevious || isSaving}
                          elmId='navigation_previous'
                          size='small'
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <Icon
                            size='medium'
                            name='angle-up'
                            iconStyle='solid'
                            color='inherit'
                            style={{
                              marginRight: '6px',
                              verticalAlign: 'middle',
                            }}
                          />
                          Previous
                        </Button>
                        <Text
                          size='tiny'
                          color='gray'
                          elmId='navigation_status'
                        >
                          {(currentIndex + 1).toLocaleString()} of{' '}
                          {totalCount.toLocaleString()}
                        </Text>
                        <Button
                          onClick={async () => {
                            const saved = await saveRating()
                            if (saved) {
                              onNext()
                            }
                          }}
                          disabled={!hasNext || isSaving}
                          elmId='navigation_next'
                          size='small'
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Next
                          <Icon
                            size='medium'
                            name='angle-down'
                            iconStyle='solid'
                            color='inherit'
                            style={{
                              marginLeft: '6px',
                              verticalAlign: 'middle',
                            }}
                          />
                        </Button>
                      </div>

                      <div className={cx('done-section')}>
                        <Button
                          onClick={async () => {
                            const saved = await saveRating()
                            if (saved) {
                              handleOnCloseAndReset()
                            }
                          }}
                          role='primary'
                          elmId='modal_done_button'
                          size='small'
                          disabled={isSaving}
                        >
                          Done
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className={cx('right-column')}>
                    <Text
                      size='huge'
                      weight='heavy'
                      className={cx('section-title')}
                    >
                      Trace
                    </Text>
                    <Trace
                      evalGroup={evaluationData}
                      method={selectedMethod}
                      containerRef={traceContainerRef}
                    />
                    {!isShortcutsGuideVisible && breakpoint !== 'mobile' && (
                      <div className={cx('right-footer')}>
                        <span className={cx('category')}>Toggle Help:</span>
                        <kbd>/</kbd>
                      </div>
                    )}
                  </div>
                </div>
                {/* Keyboard Shortcuts Guide */}
                <KeyboardShortcutsGuide isVisible={isShortcutsGuideVisible} />
              </div>
            )}
          </div>
        </Modal.Content>
      </Modal>
      <UnsavedChangesModal
        isModalOpen={isConfirmModalOpen}
        onContinueWithoutSaving={onContinueWithoutSaving}
        onKeepEditing={onKeepEditing}
        onSaveAndContinue={onSaveAndContinue}
        allowSaveAndContinue
        allowContinueWithoutSaving
        headerContent='Your edits have not been saved'
        modalContent='Would you like to save your rating information?'
        continueWithoutSavingText='Leave without saving'
        keepEditingText='No, but keep editing'
        saveAndContinueText='Yes, save and exit'
      />
    </>
  )
}

const mapStateToProps = (store: { user: User }) => ({
  user: store.user, // eslint-disable-line
})

export default connect(mapStateToProps)(EvaluationDetailModal)
